import { useEffect, useId, useRef, useState, type FormEvent, type KeyboardEvent } from 'react';
import {
  ArrowLeft,
  ArrowUpRight,
  CheckCircle2,
  ChevronDown,
  Eye,
  EyeOff,
  Heart,
  Library,
  LoaderCircle,
  LockKeyhole,
  LogOut,
  Mail,
  RefreshCw,
  Settings2,
  ShieldCheck,
  UserRound,
} from 'lucide-react';
import type { AccountUser, AuthValues } from '../types';
import { Dialog } from './Dialog';
import { PageLink } from './PageLink';
import { Brand } from './Brand';
import type { AppPage } from '../hooks/useAppNavigation';
import { PASSWORD_HINT, PASSWORD_MAX_CHARS, passwordProblem } from '../lib/password';

function PasswordField({
  label,
  value,
  onChange,
  autoComplete,
  disabled,
  minimum = 1,
  hint,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  autoComplete: 'current-password' | 'new-password';
  disabled: boolean;
  minimum?: number;
  hint?: string;
}) {
  const id = useId();
  const hintId = `${id}-hint`;
  const [visible, setVisible] = useState(false);
  return (
    <div className="auth-field">
      <label htmlFor={id}>{label}</label>
      <div className="auth-input-wrap">
        <LockKeyhole size={18} />
        <input
          id={id}
          name={id}
          type={visible ? 'text' : 'password'}
          autoComplete={autoComplete}
          value={value}
          onChange={(event) => onChange(event.target.value)}
          required
          placeholder={
            autoComplete === 'new-password' ? `6–${PASSWORD_MAX_CHARS} 个字符` : undefined
          }
          minLength={minimum}
          maxLength={PASSWORD_MAX_CHARS}
          aria-describedby={hint ? hintId : undefined}
          disabled={disabled}
        />
        <button
          type="button"
          onClick={() => setVisible(!visible)}
          aria-label={`${visible ? '隐藏' : '显示'}${label}`}
          disabled={disabled}
        >
          {visible ? <EyeOff size={18} /> : <Eye size={18} />}
        </button>
      </div>
      {hint && (
        <p className="auth-field-hint" id={hintId}>
          {hint}
        </p>
      )}
    </div>
  );
}

export function AuthDialog({
  initialMode = 'login',
  recovery = false,
  guestFavorites,
  guestOwned,
  busy,
  onClose,
  onSubmit,
  onReset,
  onUpdatePassword,
  onResend,
}: {
  initialMode?: 'login' | 'register';
  recovery?: boolean;
  guestFavorites: number;
  guestOwned: number;
  busy: boolean;
  onClose: () => void;
  onSubmit: (
    kind: 'login' | 'register',
    values: AuthValues,
  ) => Promise<{ status: 'signed-in' | 'confirm-email' }>;
  onReset: (email: string) => Promise<void>;
  onUpdatePassword: (password: string) => Promise<void>;
  onResend: (email: string) => Promise<void>;
}) {
  const [mode, setMode] = useState<'login' | 'register' | 'reset' | 'recovery'>(() =>
    recovery ? 'recovery' : initialMode,
  );
  const [email, setEmail] = useState(''),
    [displayName, setDisplayName] = useState('');
  const [password, setPassword] = useState(''),
    [confirmation, setConfirmation] = useState('');
  const [error, setError] = useState(''),
    [notice, setNotice] = useState('');
  const [resendable, setResendable] = useState(false);
  const [working, setWorking] = useState(false);
  const waiting = busy || working;
  const emailId = useId(),
    nameId = useId();
  useEffect(() => {
    if (!recovery) return;
    setMode('recovery');
    setError('');
    setNotice('');
    setPassword('');
    setConfirmation('');
  }, [recovery]);
  function switchMode(next: typeof mode) {
    setMode(next);
    setError('');
    setNotice('');
    setResendable(false);
    setPassword('');
    setConfirmation('');
  }
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    setNotice('');
    setWorking(true);
    try {
      if (mode === 'reset') {
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim()))
          throw new Error('请输入有效的邮箱地址。');
        await onReset(email);
        setNotice('如果该邮箱已注册，重置链接已发送，请查收邮件。');
        return;
      }
      if (mode === 'recovery') {
        const problem = passwordProblem(password);
        if (problem) throw new Error(problem);
        if (password !== confirmation) throw new Error('两次输入的密码不一致。');
        await onUpdatePassword(password);
        setNotice('密码已更新，请使用新密码登录。');
        return;
      }
      if (mode === 'register') {
        if (!displayName.trim()) throw new Error('请填写昵称。');
        const problem = passwordProblem(password);
        if (problem) throw new Error(problem);
        if (password !== confirmation) throw new Error('两次输入的密码不一致。');
        const result = await onSubmit('register', {
          email,
          password,
          displayName,
        });
        if (result.status === 'confirm-email')
          setNotice('确认邮件已发送，请点击邮件中的链接完成验证，然后回到本页刷新。');
        return;
      }
      await onSubmit('login', { email, password });
    } catch (submitError) {
      const message = (submitError as Error).message;
      setError(message);
      setResendable(mode === 'login' && /确认链接/.test(message));
    } finally {
      setWorking(false);
    }
  }
  async function resend() {
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError('请先填写有效的邮箱地址。');
      return;
    }
    setWorking(true);
    setError('');
    try {
      await onResend(email);
      setNotice('确认邮件已重新发送，请查收。');
      setResendable(false);
    } catch (resendError) {
      setError((resendError as Error).message);
    } finally {
      setWorking(false);
    }
  }
  const title =
    mode === 'login'
      ? '登录幕间'
      : mode === 'register'
        ? '创建幕间账号'
        : mode === 'recovery'
          ? '设置新密码'
          : '找回账号';

  return (
    <Dialog onClose={onClose} dismissible={!waiting} className="auth-dialog" title={title}>
      <aside className="auth-story">
        <img src="/art/verdant.jpg" alt="" />
        <div className="auth-story-shade" />
        <Brand light />
        <div className="auth-story-copy">
          <h2>
            让喜欢，
            <br />
            成为日常。
          </h2>
        </div>
      </aside>
      <div className="auth-content">
        {mode === 'reset' ? (
          <button
            className="auth-back"
            type="button"
            disabled={waiting}
            onClick={() => switchMode('login')}
          >
            <ArrowLeft size={16} />
            返回登录
          </button>
        ) : mode === 'login' || mode === 'register' ? (
          <div className="auth-tabs" role="group" aria-label="账号登录方式">
            <button
              type="button"
              aria-pressed={mode === 'login'}
              onClick={() => switchMode('login')}
              disabled={waiting}
            >
              登录
            </button>
            <button
              type="button"
              aria-pressed={mode === 'register'}
              onClick={() => switchMode('register')}
              disabled={waiting}
            >
              创建账号
            </button>
          </div>
        ) : null}
        <h2>
          {mode === 'login'
            ? '欢迎回到幕间'
            : mode === 'register'
              ? '创建你的幕间账号'
              : mode === 'recovery'
                ? '设置新密码'
                : '找回你的账号'}
        </h2>
        {mode === 'reset' && (
          <p className="auth-description">输入注册邮箱，我们会发送设置新密码的链接。</p>
        )}
        {mode === 'recovery' && <p className="auth-description">请设置 6–72 个字符的新密码。</p>}
        <form onSubmit={submit} className="auth-form">
          {mode === 'register' && (
            <div className="auth-field">
              <label htmlFor={nameId}>昵称</label>
              <div className="auth-input-wrap">
                <UserRound size={18} />
                <input
                  id={nameId}
                  name="nickname"
                  autoComplete="nickname"
                  value={displayName}
                  onChange={(event) => setDisplayName(event.target.value)}
                  required
                  maxLength={24}
                  disabled={waiting}
                />
              </div>
            </div>
          )}
          {mode !== 'recovery' && (
            <div className="auth-field">
              <label htmlFor={emailId}>邮箱</label>
              <div className="auth-input-wrap">
                <Mail size={18} />
                <input
                  id={emailId}
                  name="email"
                  type="email"
                  inputMode="email"
                  autoComplete="username"
                  value={email}
                  onChange={(event) => {
                    setEmail(event.target.value);
                    setError('');
                  }}
                  placeholder="你的常用邮箱"
                  required
                  maxLength={254}
                  disabled={waiting}
                  autoFocus
                />
              </div>
            </div>
          )}
          {mode !== 'reset' && (
            <PasswordField
              label={mode === 'recovery' ? '新密码' : '密码'}
              value={password}
              onChange={setPassword}
              autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
              disabled={waiting}
              minimum={mode === 'login' ? 1 : 6}
              hint={mode === 'login' ? undefined : PASSWORD_HINT}
            />
          )}
          {(mode === 'register' || mode === 'recovery') && (
            <PasswordField
              label="确认密码"
              value={confirmation}
              onChange={setConfirmation}
              autoComplete="new-password"
              disabled={waiting}
              minimum={6}
            />
          )}
          {mode === 'login' && (
            <div className="auth-options">
              <button
                type="button"
                className="auth-forgot"
                disabled={waiting}
                onClick={() => switchMode('reset')}
              >
                忘记密码？
              </button>
            </div>
          )}
          {(mode === 'login' || mode === 'register') && (guestFavorites > 0 || guestOwned > 0) && (
            <p className="auth-merge-note">
              本机的 {guestFavorites} 幅收藏、{guestOwned} 幅已拥有作品将保存到此账号。
            </p>
          )}
          {error && (
            <p className="auth-error" role="alert">
              {error}
            </p>
          )}
          {resendable && (
            <button type="button" className="auth-forgot" disabled={waiting} onClick={resend}>
              {waiting ? '发送中…' : '重新发送确认邮件'}
            </button>
          )}
          {notice && (
            <p className="auth-notice" role="status">
              {notice}
            </p>
          )}
          <button className="button button-dark auth-submit" type="submit" disabled={waiting}>
            {waiting ? <LoaderCircle size={18} className="spin" /> : null}
            {mode === 'login'
              ? '登录'
              : mode === 'register'
                ? '创建账号'
                : mode === 'recovery'
                  ? '更新密码'
                  : '发送重置邮件'}
            {!waiting && <ArrowUpRight size={18} />}
          </button>
        </form>
      </div>
    </Dialog>
  );
}

export function AccountMenu({
  page,
  favoriteCount,
  ownedCount,
  user,
  loading,
  busy,
  onLogin,
  onLogout,
  onSettings,
  navigate,
}: {
  page: AppPage;
  favoriteCount: number;
  ownedCount: number;
  user: AccountUser | null;
  loading: boolean;
  busy: boolean;
  onLogin: () => void;
  onLogout: () => Promise<void>;
  onSettings: () => void;
  navigate: (href: string) => void;
}) {
  const [open, setOpen] = useState(false);
  const root = useRef<HTMLDivElement>(null),
    trigger = useRef<HTMLButtonElement>(null);
  useEffect(() => {
    setOpen(false);
  }, [user?.id]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!root.current?.contains(event.target as Node)) setOpen(false);
    };
    document.addEventListener('pointerdown', outside);
    return () => document.removeEventListener('pointerdown', outside);
  }, [open]);
  function keydown(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      setOpen(false);
      trigger.current?.focus();
      event.stopPropagation();
    }
    if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key) && open) {
      event.preventDefault();
      const controls = Array.from(
        root.current?.querySelectorAll<HTMLElement>('[role="menuitem"]') ?? [],
      );
      const at = controls.indexOf(document.activeElement as HTMLElement);
      const next =
        event.key === 'Home'
          ? 0
          : event.key === 'End'
            ? controls.length - 1
            : (at + (event.key === 'ArrowDown' ? 1 : -1) + controls.length) % controls.length;
      controls[next]?.focus();
    }
  }
  if (!user)
    return (
      <button
        className="account-login"
        onClick={onLogin}
        disabled={loading || busy}
        aria-label="登录或注册"
      >
        <UserRound size={18} />
        <span>登录</span>
      </button>
    );
  const go = (href: string) => {
    setOpen(false);
    navigate(href);
  };
  return (
    <div
      className="account-control"
      ref={root}
      onKeyDown={keydown}
      onBlur={(event) => {
        if (!event.currentTarget.contains(event.relatedTarget as Node)) setOpen(false);
      }}
    >
      <button
        className="account-trigger"
        ref={trigger}
        onClick={() => setOpen(!open)}
        aria-label={`账号菜单：${user.displayName}`}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={busy}
      >
        <span className="account-avatar">{Array.from(user.displayName)[0]?.toUpperCase()}</span>
        <span className="account-display-name">{user.displayName}</span>
        <span className="account-mobile-label">我的账号</span>
        <ChevronDown size={14} />
      </button>
      {open && (
        <div className="account-dropdown" role="menu" aria-label="账号菜单">
          <div className="account-menu-identity">
            <strong>{user.displayName}</strong>
            <span>{user.email}</span>
          </div>
          <PageLink
            href="/favorites"
            navigate={go}
            role="menuitem"
            aria-label="我的收藏"
            aria-current={page === 'favorites' ? 'page' : undefined}
          >
            <Heart size={17} />
            <span>我的收藏</span>
            <span className="account-menu-count" aria-hidden="true">
              {favoriteCount}
            </span>
          </PageLink>
          <PageLink
            href="/owned"
            navigate={go}
            role="menuitem"
            aria-label="我拥有的"
            aria-current={page === 'library' ? 'page' : undefined}
          >
            <Library size={17} />
            <span>我拥有的</span>
            <span className="account-menu-count" aria-hidden="true">
              {ownedCount}
            </span>
          </PageLink>
          <button
            role="menuitem"
            onClick={() => {
              setOpen(false);
              onSettings();
            }}
          >
            <Settings2 size={17} />
            账号与安全
          </button>
          <button
            className="account-logout"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              void onLogout();
            }}
          >
            <LogOut size={17} />
            退出登录
          </button>
        </div>
      )}
    </div>
  );
}

export function AccountSettingsDialog({
  user,
  busy,
  onClose,
  onProfile,
  onPassword,
  onVerify,
  notify,
}: {
  user: AccountUser;
  busy: boolean;
  onClose: () => void;
  onProfile: (name: string) => Promise<unknown>;
  onPassword: (current: string, next: string) => Promise<unknown>;
  onVerify: () => Promise<unknown>;
  notify: (message: string) => void;
}) {
  const [section, setSection] = useState<'profile' | 'password'>('profile');
  const [name, setName] = useState(user.displayName),
    [current, setCurrent] = useState(''),
    [next, setNext] = useState(''),
    [confirm, setConfirm] = useState(''),
    [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const nameId = useId();
  async function submit(event: FormEvent) {
    event.preventDefault();
    setError('');
    try {
      if (section === 'profile') {
        await onProfile(name);
        notify('昵称已更新。');
      } else {
        if (next !== confirm) {
          setError('两次输入的新密码不一致。');
          return;
        }
        await onPassword(current, next);
        setCurrent('');
        setNext('');
        setConfirm('');
        notify('密码已更新，其他设备已退出登录。');
      }
    } catch (error) {
      setError((error as Error).message);
    }
  }
  return (
    <Dialog
      title="账号与安全"
      onClose={onClose}
      dismissible={!busy && !sending}
      className="account-settings-dialog"
    >
      <span className="section-kicker">你的幕间账号</span>
      <h2>账号与安全</h2>
      <div className="settings-identity">
        <span className="account-avatar">{Array.from(user.displayName)[0]?.toUpperCase()}</span>
        <div>
          <strong>{user.displayName}</strong>
          <span>{user.email}</span>
        </div>
      </div>
      <div className="settings-email-status">
        <span>
          {user.emailVerified ? <CheckCircle2 size={16} /> : <Mail size={16} />}
          {user.emailVerified ? '邮箱已验证' : '邮箱尚未验证'}
        </span>
        {!user.emailVerified && (
          <button
            type="button"
            disabled={busy || sending}
            onClick={async () => {
              setError('');
              setSending(true);
              try {
                await onVerify();
                notify('确认邮件已发送，请查收邮箱。');
              } catch (verifyError) {
                setError((verifyError as Error).message);
              } finally {
                setSending(false);
              }
            }}
          >
            {sending ? '发送中…' : '重新发送确认邮件'}
          </button>
        )}
      </div>
      <div className="auth-tabs" role="group" aria-label="账号设置分类">
        <button
          aria-pressed={section === 'profile'}
          disabled={busy}
          onClick={() => {
            setSection('profile');
            setError('');
          }}
        >
          个人资料
        </button>
        <button
          aria-pressed={section === 'password'}
          disabled={busy}
          onClick={() => {
            setSection('password');
            setError('');
          }}
        >
          密码与安全
        </button>
      </div>
      <form className="auth-form" onSubmit={submit}>
        {section === 'profile' ? (
          <>
            <div className="auth-field">
              <label htmlFor={nameId}>昵称</label>
              <div className="auth-input-wrap">
                <UserRound size={18} />
                <input
                  id={nameId}
                  autoComplete="nickname"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  maxLength={24}
                  required
                  disabled={busy}
                />
              </div>
            </div>
          </>
        ) : (
          <>
            <PasswordField
              label="当前密码"
              value={current}
              onChange={setCurrent}
              autoComplete="current-password"
              disabled={busy}
            />
            <PasswordField
              label="新密码"
              value={next}
              onChange={setNext}
              autoComplete="new-password"
              minimum={6}
              hint={PASSWORD_HINT}
              disabled={busy}
            />
            <p className="auth-field-help">修改后，其他设备需要重新登录。</p>
            <PasswordField
              label="确认新密码"
              value={confirm}
              onChange={setConfirm}
              autoComplete="new-password"
              minimum={6}
              disabled={busy}
            />
          </>
        )}
        {error && (
          <p className="auth-error" role="alert">
            {error}
          </p>
        )}
        <button
          type="submit"
          className="button button-dark auth-submit"
          disabled={busy || (section === 'profile' && name.trim() === user.displayName)}
        >
          {busy ? <LoaderCircle size={18} className="spin" /> : <ShieldCheck size={18} />}
          {section === 'profile' ? '保存昵称' : '更新密码'}
        </button>
      </form>
    </Dialog>
  );
}

export function AccountAccessGate({
  loading,
  error,
  onRetry,
  onLogin,
  navigate,
}: {
  loading: boolean;
  error: string;
  onRetry: () => void;
  onLogin: () => void;
  navigate: (href: string) => void;
}) {
  return (
    <section
      className="account-access page-width"
      aria-labelledby="account-access-title"
      aria-busy={loading}
    >
      <div className="account-access-art" aria-hidden="true">
        <img src="/art/verdant.jpg" alt="" />
        <span>
          让喜欢，
          <br />
          随你同行。
        </span>
      </div>
      <div className="account-access-copy">
        <span className="section-kicker">你的幕间</span>
        <h1 id="account-access-title">让喜欢，有处安放。</h1>
        {loading ? (
          <p role="status">
            <LoaderCircle size={20} className="spin" />
            正在确认登录状态…
          </p>
        ) : error ? (
          <>
            <p role="alert">暂时无法确认登录状态。{error}</p>
            <button className="button button-dark" onClick={onRetry}>
              重新加载 <RefreshCw size={17} />
            </button>
          </>
        ) : (
          <>
            <p>
              登录幕间，回到属于你的灵感空间。
              <br />
              换一块屏幕，喜欢依然在。
            </p>
            <button className="button button-dark" onClick={onLogin}>
              登录 / 创建账号 <ArrowUpRight size={18} />
            </button>
          </>
        )}
        <PageLink className="account-access-back" href="/#discover" navigate={navigate}>
          先逛逛画廊 <ArrowUpRight size={16} />
        </PageLink>
      </div>
    </section>
  );
}
